/**
 * One licence for every importer.
 *
 * pdf.tomake.design sells Pro through Lemon Squeezy and keeps the truth about
 * licences in its own D1: the webhook creates a key on purchase, the key is
 * verified and activated against a Figma user id, and any plugin that knows
 * that id can ask for the plan. So every tomake.design importer THAT CHARGES is a client of
 * that one API, and a licence bought for one unlocks all of them — the designer
 * never re-enters the key, because the sandbox hands the UI `figma.currentUser`
 * and the first thing the UI does is ask the server about it.
 *
 * The free tier is five imports a month per plugin, counted here the way
 * pdf.tomake.design counts its own: in the plugin's storage, reset at the turn
 * of the month, reported to the server for the record. Each plugin's UI lives
 * on its own origin, so the count is per plugin by construction. Clearing site
 * data resets it, as it does for pdf.tomake.design — the gate is a nudge, the
 * licence is the product.
 */

import { LICENSE_LINKS } from './links';

export { LICENSE_LINKS, licenseLinkUrl } from './links';
export type { LicenseLinkName } from './links';

export type Plan = 'free' | 'pro' | 'beta';

export interface LicenseUser {
  id: string;
  name: string;
}

export interface StoredLicense {
  key: string;
  planId: 'pro';
  status: 'active';
  maxUsers: number;
  userId: string | null;
  /** ISO date, or null for a lifetime licence. */
  expiresAt: string | null;
  activatedAt: string;
}

/**
 * What the free tier counts, and what it does not.
 *
 * `editable` is an import that produces Figma layers you can select and edit: vector paths,
 * text, shapes. It is the product, and it is what the five a month are about.
 *
 * `image` places the page as a picture. It is unlimited on every plan, because an image import
 * is a raster the browser already produced and gating it would push people away from the
 * cheapest, most reliable path this software has. The licence server has always worked this
 * way (`isGated` in the usage route); this type is the client finally saying the same thing.
 */
export type ImportKind = 'editable' | 'image';

export interface Gate {
  allowed: boolean;
  plan: Plan;
  used: number;
  limit: number;
  remaining: number;
  /** `YYYY-MM` the count belongs to. */
  month: string;
  /** Pages ONE free editable import may carry. `Infinity` on Pro. */
  pageLimit: number;
  /** Why the import is refused, when it is. */
  reason?: string;
}

/** What the caller is about to do, so {@link Licensing.gate} can answer about THAT import. */
export interface GateRequest {
  /** Default `editable`: the gated kind, so an unspecified call is asked the stricter question. */
  kind?: ImportKind;
  /** Pages or artboards the import would place. Checked against {@link Gate.pageLimit}. */
  pages?: number;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface LicensingOptions {
  /** This plugin's slug: the per-plugin usage key, and `import_mode` in the server's record. */
  app: string;
  /** The plugin's name as a person reads it, for the gate's message. */
  productName?: string;
  /** The licence API; pdf.tomake.design's Pages Functions by default. */
  apiBase?: string;
  checkoutUrl?: string;
  /**
   * BETA: no gate, no counting, nothing to buy. See {@link Licensing.gate}.
   *
   * A plugin still finding its shape should not be metered, and must not spend an allowance
   * that is shared with the ones that are. An import from a beta app is reported to the server
   * under its own mode so the dataset keeps it, and the server counts nothing it is not asked
   * to gate.
   *
   * A real Pro licence still wins: someone who has paid sees Pro here, not beta.
   */
  beta?: boolean;
  freeImportsPerMonth?: number;
  /** Pages one free editable import may carry; keep in step with the server. */
  freePagesPerImport?: number;
  /** Override the free-plan converter ceiling, for an app whose own limit is already lower. */
  freeMaxConvertBytes?: number;
  storage?: StorageLike | null;
  fetch?: typeof fetch;
  now?: () => Date;
}

/**
 * The licence server for the whole family: `apps/licence-api`, a Worker of its own.
 *
 * It was `https://pdfs.tomake.design/api` until 2026-10-30. That host is served by a Pages
 * project built from a SEPARATE repository, so every fix made to the licence API in this
 * monorepo — the rate limits that key on the caller rather than on a field the caller writes,
 * the limit on activation attempts, the input bounds — was deployed somewhere nothing called.
 * A plugin asking the old host still got the old code.
 *
 * `licence.tomake.design` is this repository's own service and matches `https://*.tomake.design`
 * in every plugin's `networkAccess.allowedDomains`, so no manifest changes and no republish.
 * The old host keeps answering for whatever still points at it; nothing was moved or taken
 * away from it.
 */
export const LICENSE_API = 'https://licence.tomake.design/api';
/** The one Lemon Squeezy product; the seat count is picked on the checkout page. */
export const CHECKOUT_URL = LICENSE_LINKS.checkout;
/**
 * Five EDITABLE imports a month, counted PER PERSON ACROSS EVERY PLUGIN rather than five in
 * each. One licence means one allowance; eleven importers each handing out five would be 55.
 *
 * The count cannot live in the browser alone. Each plugin's UI is served from its own origin,
 * so `localStorage` in CAD to Design is a different store from the one in Visio to Design and
 * neither can read the other. The shared number is the licence server's `usage_tracking` row,
 * keyed by Figma user and month and blind to which plugin sent the import; {@link
 * Licensing.sync} adopts it. What is stored locally is a cache of that, which also keeps the
 * gate working offline and between syncs.
 */
export const FREE_IMPORTS_PER_MONTH = 5;

/**
 * Pages one FREE editable import may carry. Pro has no limit.
 *
 * Matches `FREE_TIER_VECTOR_PAGE_LIMIT` on the licence server, which is the number actually
 * enforced. The client's job here is to refuse before the work rather than after it, and to
 * say the same thing the server would — a gate that disagrees with the server either blocks
 * imports the server would allow or promises ones it will refuse.
 */
export const FREE_PAGES_PER_IMPORT = 1;

/**
 * The largest file a FREE plan may send to the SHARED CONVERTER. Pro has no such ceiling.
 *
 * WHAT THIS DOES NOT COVER, and the reason the name says `CONVERT`. Most importers here do
 * their work in WebAssembly in the designer's own browser: PSD, PDF, EPS, HEIC, screenshots,
 * vector tracing. Those cost nothing to run however large the file is — the compute is the
 * user's. Capping them would be charging for something that was never an expense, so they are
 * not capped, and a file that skips the converter inside a converter-backed importer (a PDF
 * dropped on CAD to Design, say) is not capped either.
 *
 * What does cost money is `office-to-design-converter`: a LibreOffice container, metered in
 * container minutes, shared by six importers. This is the ceiling on what a free plan may ask
 * it to do.
 *
 * 50MB is a product decision, not a number derived from anything, and it is worth being clear
 * about what it gives up. The Worker's cache ceiling is 25MB (`MAX_CACHEABLE_BYTES` in its
 * index.ts): at or below it a document is hashed and its PDF kept, so a repeat import is an
 * object read costing nothing. Above it nothing can be cached and every import pays for a full
 * LibreOffice run. So a free file between 25MB and 50MB DOES land on the expensive path, which
 * an earlier version of this constant was set to prevent by matching the two numbers exactly.
 *
 * What bounds that instead is the Worker's own second allowance: anything it cannot cache is
 * limited to 3 conversions a minute per IP (`LARGE_CONVERT_LIMIT`), separately from the general
 * 10. The expensive path is therefore metered whoever walks down it, free or paid, which is the
 * control that actually holds — a plan ceiling is enforced in the UI and a determined caller
 * never sees it.
 *
 * WHERE THIS IS ENFORCED, AND WHERE IT IS NOT. In the UI, before the upload. The converter
 * Worker cannot apply it: an unauthenticated POST carries nothing that says which plan sent
 * it, so a hand-made request is still bounded only by the Worker's own 100MB ceiling and its
 * rate limits. Making this a server-side rule needs the signed token that
 * `apps/office-to-design/worker/index.ts` already names as the eventual answer. Until then it
 * is a product rule that holds for everyone using the plugin, not a security boundary.
 */
export const FREE_MAX_CONVERT_BYTES = 50 * 1024 * 1024;

const megabytes = (bytes: number): string => {
  const value = bytes / 1024 / 1024;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

/**
 * What a person is told when a file is too big for the converter, written ONCE for the six
 * importers that use it.
 *
 * The two cases need different words and the difference matters. Over the app's own ceiling
 * there is nothing to sell: the file cannot be imported on any plan, and offering Pro would be
 * taking money for something that still will not work. Over the free cap it is a limit Pro
 * genuinely removes, so the message says so and names both numbers — a refusal that does not
 * say how far over you are is one you cannot act on.
 *
 * @param size      the file, in bytes
 * @param cap       the ceiling actually applied: the smaller of the plan's and the app's
 * @param appLimit  the app's own ceiling, whatever the plan
 */
export function tooLargeMessage(size: number, cap: number, appLimit: number): string {
  /*
   * Pro is offered only when buying it would ACTUALLY let this file through: the plan is what
   * is holding it back (`cap < appLimit`) AND the file fits under the app's own ceiling
   * anyway. The second half was missing at first, so a 300MB file on a 100MB importer was
   * told that Pro lifts the cap. It does not; nothing does. A test caught it.
   */
  if (cap >= appLimit || size > appLimit) return `That file is too large (max ${megabytes(appLimit)} MB).`;
  return `That file is ${megabytes(size)} MB. Free imports are capped at ${megabytes(cap)} MB — Pro lifts the cap here and in every tomake.design plugin.`;
}

const LICENSE_KEY = 'tomake_license';
/**
 * ONE key, not one per plugin. The allowance is the person's, not the plugin's — see
 * {@link FREE_IMPORTS_PER_MONTH}. It was `tomake_usage:${app}`, which gave every importer its
 * own five.
 */
const USAGE_KEY = 'tomake_usage';

export type ActivateResult = { ok: true; license: StoredLicense } | { ok: false; error: string };

export interface Licensing {
  readonly app: string;
  readonly checkoutUrl: string;
  readonly limit: number;
   /**
   * The ceiling the CURRENT plan puts on one file SENT TO THE SHARED CONVERTER, in bytes: the
   * free cap, or `Infinity` on Pro. `Infinity` rather than a large number so a caller takes
   * the smaller of this and its own limit without special-casing the plan.
   *
   * Callers that convert in the browser should not consult this at all. See
   * {@link FREE_MAX_CONVERT_BYTES}.
   */
  maxConvertBytes(): number;
  plan(): Plan;
  license(): StoredLicense | null;
  /**
   * Whether an import may proceed. Called with no argument it answers about the stricter case
   * — an editable import of one page — which is what the UI shows as the standing state.
   */
  gate(request?: GateRequest): Gate;
  /**
   * Count a completed import; the server is told as well, best effort.
   *
   * Only an `editable` import is counted. An `image` import is free on every plan, so counting
   * it locally would refuse a sixth image the server would happily allow.
   */
  recordImport(user: LicenseUser | null, pages?: number, kind?: ImportKind): Gate;
  /**
   * Ask the server what it knows about this Figma user: the licence, and the month's import
   * count ACROSS EVERY PLUGIN, which is adopted as the local count. Returns true when
   * anything the UI shows has changed.
   */
  sync(user: LicenseUser | null): Promise<boolean>;
  activate(key: string, user: LicenseUser | null): Promise<ActivateResult>;
  deactivate(user: LicenseUser | null): Promise<void>;
  /** Called whenever plan or usage changes; returns the unsubscribe. */
  subscribe(listener: () => void): () => void;
}

interface Usage {
  month: string;
  imports: number;
}

function monthOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * `localStorage`, but only when it is genuinely safe to touch. `null` when it is not.
 *
 * THIS MUST BE A TRY/CATCH, AND `typeof` IS NOT ENOUGH. Figma loads a bundled plugin UI —
 * `figma.showUI(__html__)` — from a `data:` URL, and a `data:` URL has an opaque origin where
 * reading `localStorage` throws:
 *
 *     SecurityError: Failed to read the 'localStorage' property from 'Window':
 *     Storage is disabled inside 'data:' URLs
 *
 * `typeof localStorage !== 'undefined'` reads like a guard and is not one. The identifier
 * resolves to a property of the global object, so the getter runs and throws before `typeof`
 * ever sees a value. Every importer calls `createLicensing` at module scope, so the throw took
 * the whole UI module down at load: a plugin window that never rendered, with the real cause
 * one line into a package the app does not own.
 *
 * The write probe is the second half. Safari in private browsing hands back a `localStorage`
 * object that exists, reads fine, and throws on `setItem`. Checking only for presence would
 * pass that and then fail on the first import somebody completed.
 *
 * WHAT IS LOST when this returns null: the licence and the month's count live in memory and go
 * when the plugin window closes. That is survivable precisely because the server is the real
 * record — {@link Licensing.sync} re-reads both on the next handshake, which is also what
 * makes the allowance shared across plugins in the first place.
 */
function browserStorage(): StorageLike | null {
  try {
    const store = (globalThis as { localStorage?: StorageLike }).localStorage;
    if (!store) return null;
    const probe = '__tomake_storage_probe__';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch {
    return null;
  }
}

function memoryStorage(): StorageLike {
  const m = new Map<string, string>();
  return { getItem: (k) => m.get(k) ?? null, setItem: (k, v) => void m.set(k, v), removeItem: (k) => void m.delete(k) };
}

function readJson<T>(storage: StorageLike, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/** The server wraps some replies in `data`; some it does not. */
function unwrap(json: unknown): Record<string, unknown> {
  if (json && typeof json === 'object') {
    const o = json as Record<string, unknown>;
    return (o.data && typeof o.data === 'object' ? o.data : o) as Record<string, unknown>;
  }
  return {};
}

export function createLicensing(options: LicensingOptions): Licensing {
  const app = options.app;
  const productName = options.productName ?? app;
  const api = (options.apiBase ?? LICENSE_API).replace(/\/$/, '');
  const checkoutUrl = options.checkoutUrl ?? CHECKOUT_URL;
  const beta = options.beta === true;
  const limit = options.freeImportsPerMonth ?? FREE_IMPORTS_PER_MONTH;
  const pagesPerImport = options.freePagesPerImport ?? FREE_PAGES_PER_IMPORT;
  const freeMaxConvertBytes = options.freeMaxConvertBytes ?? FREE_MAX_CONVERT_BYTES;
  const storage: StorageLike = options.storage ?? browserStorage() ?? memoryStorage();
  const doFetch = options.fetch ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined);
  const now = options.now ?? (() => new Date());
  const listeners = new Set<() => void>();

  /**
   * The cached no-argument {@link Licensing.gate} result, and the reason it exists.
   *
   * `packages/ui`'s licence panel reads the gate through `useSyncExternalStore`, which compares
   * each snapshot to the last with `Object.is` and re-renders when they differ. `gate()` built
   * a fresh object every call, so every snapshot differed from the one before it and React
   * re-rendered until it gave up:
   *
   *     Minified React error #185 — Maximum update depth exceeded
   *
   * Caching here rather than in that component fixes every reader at once, including any
   * future one that reaches for the same hook and would hit the same wall. A gate asked about
   * a SPECIFIC import is never cached: it depends on the request, and its callers are decision
   * points rather than renders.
   */
  let snapshot: Gate | null = null;

  const notify = (): void => {
    snapshot = null;
    for (const l of listeners) l();
  };

  const license = (): StoredLicense | null => readJson<StoredLicense>(storage, LICENSE_KEY);
  const saveLicense = (l: StoredLicense | null): void => {
    if (l) storage.setItem(LICENSE_KEY, JSON.stringify(l));
    else storage.removeItem(LICENSE_KEY);
    notify();
  };

  const plan = (): Plan => {
    const l = license();
    const paid = l && l.status === 'active' && l.planId === 'pro'
      && !(l.expiresAt && new Date(l.expiresAt).getTime() < now().getTime());
    if (paid) return 'pro';
    // Checked after Pro on purpose: someone who has paid should see what they paid for, not a
    // beta badge, and their licence keeps working the day this app leaves beta.
    return beta ? 'beta' : 'free';
  };

  const maxConvertBytes = (): number => (plan() === 'pro' ? Infinity : freeMaxConvertBytes);

  const usage = (): Usage => {
    const month = monthOf(now());
    const stored = readJson<Usage>(storage, USAGE_KEY);
    if (stored && stored.month === month && typeof stored.imports === 'number') return stored;
    return { month, imports: 0 };
  };

  const computeGate = (request: GateRequest): Gate => {
    const u = usage();
    const p = plan();
    const used = u.imports;
    const kind: ImportKind = request.kind ?? 'editable';
    const pages = request.pages ?? 1;

    if (p === 'pro' || p === 'beta') {
      return { allowed: true, plan: p, used, limit: Infinity, remaining: Infinity, pageLimit: Infinity, month: u.month };
    }

    const remaining = Math.max(0, limit - used);
    const base = { plan: p, used, limit, remaining, pageLimit: pagesPerImport, month: u.month } as const;

    /*
     * An image import is free on every plan, so it is allowed whatever the count says — and
     * the count is still reported, because the panel shows the standing allowance whichever
     * mode happens to be selected.
     */
    if (kind === 'image') return { ...base, allowed: true };

    if (used >= limit) {
      return {
        ...base,
        allowed: false,
        reason: `You've used all ${limit} free imports this month. They are shared across every tomake.design plugin, not five each. Pro removes the limit — one licence, one payment.`,
      };
    }

    /*
     * The page check comes second on purpose. Someone out of imports is told that first,
     * because splitting the document would not help them; someone with imports left is told
     * the thing they can act on.
     */
    if (pages > pagesPerImport) {
      return {
        ...base,
        allowed: false,
        reason: `A free import places ${pagesPerImport} page${pagesPerImport === 1 ? '' : 's'}, and this one has ${pages}. Import fewer pages, choose image mode, or go Pro for the whole document in ${productName} and every other tomake.design plugin.`,
      };
    }

    return { ...base, allowed: true };
  };

  const gate = (request?: GateRequest): Gate => {
    if (request !== undefined) return computeGate(request);
    // Rebuilt when something changed (`notify`) or the month turned underneath a long session.
    if (snapshot === null || snapshot.month !== monthOf(now())) snapshot = computeGate({});
    return snapshot;
  };

  const post = async (path: string, body: Record<string, unknown>): Promise<{ status: number; data: Record<string, unknown> }> => {
    if (!doFetch) throw new Error('No fetch available');
    const res = await doFetch(`${api}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    let json: unknown;
    try {
      json = await res.json();
    } catch {
      json = null;
    }
    return { status: res.status, data: unwrap(json) };
  };

  return {
    app,
    checkoutUrl,
    limit,
    maxConvertBytes,
    plan,
    license,
    gate,

    recordImport(user, pages = 1, kind: ImportKind = 'editable') {
      /*
       * `import_mode` is the SERVER'S word for which kind this was, and it decides whether the
       * import counts at all: the usage route gates on `svg`/`editable` and treats everything
       * else as unlimited. This used to send the plugin's slug — `cad-to-design` — which
       * matched neither, so the server filed every import as an unlimited one and the free
       * tier was never actually enforced anywhere.
       */
      if (kind === 'editable' && !beta) {
        const u = usage();
        storage.setItem(USAGE_KEY, JSON.stringify({ month: u.month, imports: u.imports + 1 }));
        notify();
      }
      if (user?.id && doFetch) {
        // The server's record; a failure here changes nothing the designer sees.
        post('/usage/increment', {
          user_id: user.id,
          user_name: user.name || '',
          page_count: Math.max(1, pages),
          /*
           * `beta` is a mode the server does not gate on, which is the point: the import is
           * still recorded in the dataset, and it spends none of the allowance that the
           * charging plugins share. Sending `editable` here would let a beta plugin lock
           * someone out of PDF to Figma.
           */
          import_mode: beta ? 'beta' : kind === 'editable' ? 'editable' : 'images',
          // Not read by the gate; it is how the dataset tells the plugins apart.
          plugin: app,
        }).catch(() => undefined);
      }
      return gate({ kind, pages });
    },

    async sync(user) {
      if (!user?.id || !doFetch) return false;
      let data: Record<string, unknown>;
      try {
        const params = new URLSearchParams({ user_id: user.id, user_name: user.name || '' });
        const res = await doFetch(`${api}/user/status?${params}`);
        data = unwrap(await res.json());
      } catch {
        return false; // offline: trust what is stored
      }
      /*
       * THE COUNT COMES FROM HERE, and this is what makes the five actually shared.
       *
       * `usage_tracking` on the server is keyed by Figma user and month with no plugin
       * dimension, so `imports_used` is everything this person has imported anywhere this
       * month. Local storage cannot hold that: each plugin's UI is its own origin and cannot
       * read another's. So the local number is a cache, and every sync replaces it with the
       * server's — which is also how a plugin opened for the first time this month learns that
       * four imports have already gone, in a different plugin, on a different day.
       *
       * Taken whether or not the licence changed, and reported in the return value, because
       * the panel shows the remaining count and a stale one is worse than none.
       */
      let usageChanged = false;
      // A beta app shows no count, so adopting one would only make the gate it does not use
      // disagree with the server.
      const serverUsed = beta ? undefined : data.imports_used;
      if (typeof serverUsed === 'number' && Number.isFinite(serverUsed) && serverUsed >= 0) {
        const u = usage();
        if (u.imports !== serverUsed) {
          storage.setItem(USAGE_KEY, JSON.stringify({ month: u.month, imports: serverUsed }));
          usageChanged = true;
        }
      }

      const local = license();
      if (data.has_active_license && typeof data.license_key === 'string') {
        const expiresAt = typeof data.expires_at === 'string' ? data.expires_at : null;
        if (local && local.status === 'active' && local.key === data.license_key && local.expiresAt === expiresAt) {
          if (usageChanged) notify();
          return usageChanged;
        }
        saveLicense({
          key: data.license_key,
          planId: 'pro',
          status: 'active',
          maxUsers: typeof data.max_users === 'number' ? data.max_users : 1,
          userId: user.id,
          expiresAt,
          activatedAt: local?.activatedAt ?? now().toISOString(),
        });
        return true;
      }
      if (local) {
        saveLicense(null); // refunded, expired, or never real
        return true;
      }
      if (usageChanged) notify();
      return usageChanged;
    },

    async activate(rawKey, user) {
      const key = rawKey.trim();
      if (!key) return { ok: false, error: 'Please enter a licence key.' };
      try {
        const verify = await post('/license/verify', { license_key: key });
        if (!verify.data.valid) return { ok: false, error: (verify.data.message as string) || 'Invalid licence key.' };
        const activate = await post('/license/activate', { license_key: key, user_id: user?.id || 'anonymous', user_name: user?.name || 'Figma User' });
        if (activate.status === 404) return { ok: false, error: 'Invalid licence key. Please check and try again.' };
        if (activate.status === 409) return { ok: false, error: 'This licence key is already in use by another account.' };
        if (activate.status >= 400 || activate.data.success === false) return { ok: false, error: (activate.data.message as string) || 'Activation failed.' };
        const record: StoredLicense = {
          key,
          planId: 'pro',
          status: 'active',
          maxUsers: typeof verify.data.max_users === 'number' ? verify.data.max_users : 1,
          userId: user?.id ?? null,
          expiresAt: typeof verify.data.expires_at === 'string' ? verify.data.expires_at : null,
          activatedAt: now().toISOString(),
        };
        saveLicense(record);
        return { ok: true, license: record };
      } catch (error) {
        return { ok: false, error: error instanceof TypeError ? 'Network error. Please check your connection.' : `Activation failed: ${error instanceof Error ? error.message : String(error)}` };
      }
    },

    async deactivate(user) {
      const l = license();
      saveLicense(null);
      if (l?.key && user?.id) {
        try {
          await post('/license/deactivate', { license_key: l.key, user_id: user.id });
        } catch {
          // Local state is already Free; the seat frees on the next successful call or on expiry.
        }
      }
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
